import {FC, useRef, useEffect, useState} from 'react'
import {QuickAllocationModal} from './QuickAllocationModal'

type Table = {
  id: number
  environmentId: number
  tableTypeId: number
  tableNumber: string
  customCapacity?: number
  positionX: number | null
  positionY: number | null
  isActive: number
  notes: string | null
  tableType?: {
    id: number
    capacity: number
    description: string
  }
  currentStatus?: string
}

type Allocation = {
  id: number
  environmentTableId: number
  bookingId: number
  clientName: string
  people: number
  phone?: string
  email?: string
  status: string
}

type Booking = {
  id: number
  name: string
  people: number
  phone?: string
  email?: string
  bookingHourId: number
  date: string
  isAllocated?: boolean
  allocationId?: number
}

type Props = {
  tables: Table[]
  environmentName: string
  onTableClick: (table: Table) => void
  onUpdatePosition: (tableId: number, x: number, y: number) => void
  allocations?: Allocation[]
  availableBookings?: Booking[]
  onAllocateBooking?: (tableId: number, bookingId: number) => Promise<void>
  onDeallocateBooking?: (allocationId: number) => Promise<void>
  onAllocateWalkIn?: (tableIds: number[], people: number, name: string, phone: string, email: string) => Promise<void>
  onChangeAllocationStatus?: (allocationId: number, newStatus: 'allocated' | 'occupied' | 'cleaning') => Promise<void>
}

export const TableManagementMap: FC<Props> = ({
  tables,
  environmentName,
  onTableClick,
  onUpdatePosition,
  allocations = [],
  availableBookings = [],
  onAllocateBooking,
  onDeallocateBooking,
  onAllocateWalkIn,
  onChangeAllocationStatus,
}) => {
  console.log('🗺️ TableManagementMap - Total de mesas recebidas:', tables.length)
  console.log('🗺️ TableManagementMap - Reservas disponíveis:', availableBookings.length)
  console.log('🗺️ TableManagementMap - Alocações ativas:', allocations.length)
  
  const isTableAllocated = (tableId: number) => {
    return allocations.some((alloc) => alloc.environmentTableId === tableId)
  }

  const getAllocationInfo = (tableId: number) => {
    return allocations.find((alloc) => alloc.environmentTableId === tableId)
  }

  // 🎨 Função para gerar cor única baseada no bookingId
  const getUnionBorderColor = (bookingId: number): string => {
    const colors = [
      '#8B5CF6', // Roxo
      '#EC4899', // Rosa Pink
      '#F59E0B', // Laranja
      '#10B981', // Verde Esmeralda
      '#3B82F6', // Azul
      '#EF4444', // Vermelho
      '#14B8A6', // Teal
      '#F97316', // Laranja Escuro
      '#8B5A00', // Marrom
      '#6366F1', // Indigo
    ]
    // Usar bookingId como índice para garantir mesma cor para mesmo grupo
    return colors[bookingId % colors.length]
  }

  // 📝 Função para obter iniciais do nome completo
  const getClientInitials = (fullName: string): string => {
    const names = fullName.trim().split(' ')
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase()
    // Primeira letra do primeiro nome + primeira letra do último nome
    return (names[0][0] + names[names.length - 1][0]).toUpperCase()
  }

  // 🔗 Função para detectar se mesa faz parte de um grupo (múltiplas mesas para mesma reserva)
  const getUnionGroupInfo = (tableId: number) => {
    const allocation = getAllocationInfo(tableId)
    if (!allocation) return null

    // Contar quantas mesas estão alocadas para a mesma reserva
    const sameBookingAllocations = allocations.filter(
      (a) => a.bookingId === allocation.bookingId
    )

    // Só consideramos "união" se houver 2+ mesas para mesma reserva
    if (sameBookingAllocations.length > 1) {
      return {
        bookingId: allocation.bookingId,
        clientName: allocation.clientName,
        totalTables: sameBookingAllocations.length,
        borderColor: getUnionBorderColor(allocation.bookingId),
        initials: getClientInitials(allocation.clientName),
      }
    }
    return null
  }

  const mapRef = useRef<HTMLDivElement>(null)
  const [draggingTable, setDraggingTable] = useState<number | null>(null)
  const [dragOffset, setDragOffset] = useState({x: 0, y: 0})
  const [hasDragged, setHasDragged] = useState(false)
  
  // Estado para modal de alocação rápida
  const [showQuickModal, setShowQuickModal] = useState(false)
  const [selectedTableForModal, setSelectedTableForModal] = useState<Table | null>(null)

  // Configurações do grid
  const GRID_SIZE = 20 // Tamanho da célula do grid
  const TABLE_SIZE = 80 // Tamanho da mesa (largura/altura)
  const COLLISION_PADDING = 10 // Espaço mínimo entre mesas

  // Função para alinhar ao grid (snap)
  const snapToGrid = (value: number): number => {
    return Math.round(value / GRID_SIZE) * GRID_SIZE
  }

  // Função para detectar colisão com outras mesas (DESATIVADA - Manual)
  const checkCollision = (
    tableId: number,
    newX: number,
    newY: number
  ): {collides: boolean; adjustedX: number; adjustedY: number} => {
    // MODO MANUAL: Permite qualquer posição, sem verificação de colisão
    return {collides: false, adjustedX: newX, adjustedY: newY}
  }

  // Função para calcular posição inicial automática para novas mesas
  const getAutoPosition = (tableIndex: number, tableId: number): {x: number; y: number} => {
    const mapWidth = mapRef.current?.clientWidth || 1400
    const mapHeight = mapRef.current?.clientHeight || 600

    const SPACING = 30
    const MARGIN = 40

    // Calcular quantas colunas cabem
    const cols = Math.floor((mapWidth - MARGIN * 2) / (TABLE_SIZE + SPACING))
    
    // Calcular posição em grid baseada no índice
    const row = Math.floor(tableIndex / cols)
    const col = tableIndex % cols

    const x = snapToGrid(MARGIN + col * (TABLE_SIZE + SPACING))
    const y = snapToGrid(MARGIN + row * (TABLE_SIZE + SPACING))

    // Verificar se está dentro dos limites do mapa
    if (x + TABLE_SIZE > mapWidth || y + TABLE_SIZE > mapHeight) {
      // Se passou dos limites, retornar posição no canto superior esquerdo disponível
      return {x: MARGIN, y: MARGIN}
    }

    // Verificar se colide com alguma mesa JÁ POSICIONADA
    const {collides} = checkCollision(tableId, x, y)

    if (!collides) {
      return {x, y}
    }

    // Se colidiu, tentar encontrar próxima posição livre
    for (let attempt = 1; attempt < 50; attempt++) {
      const adjustedIndex = tableIndex + attempt
      const newRow = Math.floor(adjustedIndex / cols)
      const newCol = adjustedIndex % cols

      const newX = snapToGrid(MARGIN + newCol * (TABLE_SIZE + SPACING))
      const newY = snapToGrid(MARGIN + newRow * (TABLE_SIZE + SPACING))

      if (newX + TABLE_SIZE > mapWidth || newY + TABLE_SIZE > mapHeight) {
        continue // Pular se estiver fora dos limites
      }

      const {collides: collidesAttempt} = checkCollision(tableId, newX, newY)
      if (!collidesAttempt) {
        return {x: newX, y: newY}
      }
    }

    // Fallback: posição no topo
    return {x: snapToGrid(MARGIN), y: snapToGrid(MARGIN)}
  }

  const getStatusColor = (tableId: number, status?: string, isActive?: number) => {
    // Se a mesa está inativa, sempre cinza escuro
    if (isActive === 0) return '#8b9199' // Cinza escuro - Inativa
    
    // Se a mesa tem alocação, usar a cor baseada no STATUS DA ALOCAÇÃO
    const allocation = getAllocationInfo(tableId)
    if (allocation) {
      switch (allocation.status) {
        case 'occupied':
          return '#fd397a' // Rosa - Ocupada (cliente presente)
        case 'cleaning':
          return '#009ef7' // Azul - Em limpeza (cor da legenda)
        case 'allocated':
        default:
          return '#50cd89' // Verde - Alocada (aguardando cliente)
      }
    }
    
    // Mesa sem alocação = disponível (cinza claro)
    return '#e4e6ef' // Cinza claro - Disponível
  }

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'available':
        return 'Disponível'
      case 'allocated':
        return 'Alocada'
      case 'occupied':
        return 'Ocupada'
      case 'cleaning':
        return 'Limpeza'
      default:
        return 'Disponível'
    }
  }

  const handleMouseDown = (e: React.MouseEvent, table: Table) => {
    if (e.button !== 0) return // Apenas botão esquerdo

    const rect = e.currentTarget.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
    setDraggingTable(table.id)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (draggingTable === null || !mapRef.current) return

    setHasDragged(true) // Marcar que houve arrasto

    const mapRect = mapRef.current.getBoundingClientRect()
    let x = e.clientX - mapRect.left - dragOffset.x
    let y = e.clientY - mapRect.top - dragOffset.y

    // Limitar dentro dos limites do mapa
    const maxX = mapRect.width - TABLE_SIZE
    const maxY = mapRect.height - TABLE_SIZE
    x = Math.max(0, Math.min(x, maxX))
    y = Math.max(0, Math.min(y, maxY))

    // Aplicar snap to grid durante o arrasto
    x = snapToGrid(x)
    y = snapToGrid(y)

    // Atualizar posição visual temporária
    const tableEl = document.getElementById(`table-${draggingTable}`)
    if (tableEl) {
      tableEl.style.opacity = '1'
      tableEl.style.left = `${x}px`
      tableEl.style.top = `${y}px`
    }
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (draggingTable === null || !mapRef.current) return

    const mapRect = mapRef.current.getBoundingClientRect()
    let x = e.clientX - mapRect.left - dragOffset.x
    let y = e.clientY - mapRect.top - dragOffset.y

    // Limitar dentro dos limites do mapa
    const maxX = mapRect.width - TABLE_SIZE
    const maxY = mapRect.height - TABLE_SIZE
    x = Math.max(0, Math.min(x, maxX))
    y = Math.max(0, Math.min(y, maxY))

    // Aplicar snap to grid final
    x = snapToGrid(x)
    y = snapToGrid(y)

    if (hasDragged) {
      // Salvar posição no backend AUTOMATICAMENTE (sem verificar colisão)
      onUpdatePosition(draggingTable, x, y)
    }

    setDraggingTable(null)
  }

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <div>
          <h5 className='mb-0'>Mapa de Mesas - {environmentName}</h5>
          <small className='text-muted'>
            <i className='bi bi-info-circle me-1'></i>
            Arraste as mesas para posicioná-las livremente. Organize-as manualmente como preferir.
          </small>
        </div>
        <div className='d-flex gap-4 flex-wrap align-items-center'>
          <strong className='text-dark'>Legenda:</strong>
          
          <div className='d-flex align-items-center gap-2'>
            <div
              className='rounded'
              style={{
                backgroundColor: '#7239ea',
                width: '24px',
                height: '24px',
                boxShadow: '0 2px 4px rgba(114, 57, 234, 0.3)',
              }}
            ></div>
            <span className='fw-semibold text-dark'>Disponível</span>
          </div>
          
          <div className='d-flex align-items-center gap-2'>
            <div
              className='rounded'
              style={{
                backgroundColor: '#ff9f43',
                width: '24px',
                height: '24px',
                boxShadow: '0 2px 4px rgba(255, 159, 67, 0.3)',
              }}
            ></div>
            <span className='fw-semibold text-dark'>Alocada</span>
          </div>
          
          <div className='d-flex align-items-center gap-2'>
            <div
              className='rounded'
              style={{
                backgroundColor: '#fd397a',
                width: '24px',
                height: '24px',
                boxShadow: '0 2px 4px rgba(253, 57, 122, 0.3)',
              }}
            ></div>
            <span className='fw-semibold text-dark'>Ocupada</span>
          </div>
          
          <div className='d-flex align-items-center gap-2'>
            <div
              className='rounded'
              style={{
                backgroundColor: '#3699ff',
                width: '24px',
                height: '24px',
                boxShadow: '0 2px 4px rgba(54, 153, 255, 0.3)',
              }}
            ></div>
            <span className='fw-semibold text-dark'>Limpeza</span>
          </div>
          
          <div className='d-flex align-items-center gap-2'>
            <div
              className='rounded'
              style={{
                backgroundColor: '#8b9199',
                width: '24px',
                height: '24px',
                boxShadow: '0 2px 4px rgba(139, 145, 153, 0.3)',
              }}
            ></div>
            <span className='fw-semibold text-dark'>Inativa</span>
          </div>
          
          <div className='vr' style={{height: '30px'}}></div>
          
          <div className='d-flex align-items-center gap-2'>
            <div
              className='position-relative rounded'
              style={{
                width: '24px',
                height: '24px',
                backgroundColor: '#0abb87',
                border: '3px solid #047857',
                boxShadow: '0 0 12px rgba(10, 187, 135, 0.8), 0 0 24px rgba(10, 187, 135, 0.4)',
              }}
            >
              <i 
                className='bi bi-check-lg text-white' 
                style={{
                  fontSize: '12px', 
                  position: 'absolute', 
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontWeight: 'bold',
                }}
              ></i>
            </div>
            <span className='fw-bold' style={{color: '#0abb87'}}>
              Com reserva confirmada (filtro)
            </span>
          </div>
        </div>
      </div>

      <div
        ref={mapRef}
        className='border rounded'
        style={{
          width: '100%',
          height: '600px',
          position: 'relative',
          cursor: draggingTable ? 'grabbing' : 'default',
          overflow: 'hidden',
          backgroundColor: '#f8f9fa',
          backgroundImage: `
            repeating-linear-gradient(
              0deg,
              #e9ecef 0px,
              #e9ecef 1px,
              transparent 1px,
              transparent ${GRID_SIZE}px
            ),
            repeating-linear-gradient(
              90deg,
              #e9ecef 0px,
              #e9ecef 1px,
              transparent 1px,
              transparent ${GRID_SIZE}px
            )
          `,
          backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {tables.length === 0 ? (
          <div className='d-flex flex-column align-items-center justify-content-center h-100'>
            <i className='bi bi-grid-3x3-gap fs-3x text-muted mb-3'></i>
            <span className='text-muted'>Nenhuma mesa para exibir</span>
            <span className='text-muted small'>
              Clique em "Nova Mesa" para adicionar mesas ao mapa
            </span>
          </div>
        ) : (
          tables.map((table, index) => {
            // Se não tem posição definida, usar posição automática
            let x = table.positionX
            let y = table.positionY

            if (x === null || y === null || (x === 0 && y === 0)) {
              const autoPos = getAutoPosition(index, table.id)
              x = autoPos.x
              y = autoPos.y
            }

            return (
              <div
                key={table.id}
                id={`table-${table.id}`}
                className='position-absolute'
                style={{
                  left: `${x}px`,
                  top: `${y}px`,
                  cursor: 'grab',
                }}
                onMouseDown={(e) => handleMouseDown(e, table)}
                onClick={(e) => {
                  // Só processar cliques se NÃO houve arrasto
                  if (draggingTable === null && !hasDragged) {
                    e.stopPropagation()
                    console.log('🖱️ Abrindo modal com', availableBookings.length, 'reservas disponíveis')
                    // Clique único: abrir modal de alocação/desalocação rápida
                    setSelectedTableForModal(table)
                    setShowQuickModal(true)
                  }
                  // Resetar flag de arrasto para permitir próximo clique
                  setHasDragged(false)
                }}
                title={
                  isTableAllocated(table.id)
                    ? `Alocada para: ${getAllocationInfo(table.id)?.clientName} (${getAllocationInfo(table.id)?.people} pessoa(s))\n\nDica: Clique para desalocar`
                    : 'Duplo clique para alocação rápida'
                }
              >
                <div
                  className='d-flex flex-column align-items-center justify-content-center fw-bold shadow position-relative'
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '8px',
                    backgroundColor: getStatusColor(table.id, table.currentStatus, table.isActive),
                    color: '#fff',
                    border: (() => {
                      const unionInfo = getUnionGroupInfo(table.id)
                      if (unionInfo) {
                        // Mesa unida: borda colorida única do grupo
                        return `5px solid ${unionInfo.borderColor}`
                      }
                      // Mesa normal
                      return isTableAllocated(table.id)
                        ? '5px solid #047857'
                        : '3px solid rgba(255, 255, 255, 0.9)'
                    })(),
                    userSelect: 'none',
                    boxShadow: (() => {
                      const unionInfo = getUnionGroupInfo(table.id)
                      if (unionInfo) {
                        // Sombra colorida para mesas unidas
                        return `0 0 20px ${unionInfo.borderColor}80, 0 4px 15px rgba(0, 0, 0, 0.2)`
                      }
                      return isTableAllocated(table.id)
                        ? '0 0 25px rgba(10, 187, 135, 0.9), 0 0 45px rgba(10, 187, 135, 0.5), 0 4px 15px rgba(0, 0, 0, 0.2)'
                        : '0 2px 8px rgba(0, 0, 0, 0.15)'
                    })(),
                  }}
                >
                  {/* Badge para mesas unidas: iniciais do cliente */}
                  {(() => {
                    const unionInfo = getUnionGroupInfo(table.id)
                    if (unionInfo) {
                      return (
                        <div
                          className='position-absolute badge fw-bold'
                          style={{
                            bottom: '-6px',
                            left: '-6px',
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            backgroundColor: unionInfo.borderColor,
                            color: '#fff',
                            border: '2px solid white',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                            letterSpacing: '-0.5px',
                          }}
                          title={`Grupo: ${unionInfo.clientName} (${unionInfo.totalTables} mesas unidas)`}
                        >
                          {unionInfo.initials}
                        </div>
                      )
                    }
                    // Mesa normal alocada: check verde
                    if (isTableAllocated(table.id)) {
                      return (
                        <div
                          className='position-absolute badge badge-success'
                          style={{
                            top: '-10px',
                            right: '-10px',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                          }}
                        >
                          <i className='bi bi-check-lg text-white'></i>
                        </div>
                      )
                    }
                    return null
                  })()}
                  <div className='fs-6'>
                    {table.tableNumber}
                    {table.isActive === 0 && (
                      <i className='bi bi-x-circle ms-1' title='Mesa inativa'></i>
                    )}
                  </div>
                  <div className='fs-8' style={{fontSize: '11px'}}>
                    <i className='bi bi-people-fill me-1' style={{color: '#3699FF'}}></i>
                    {table.customCapacity || table.tableType?.capacity || '-'}
                  </div>
                  <div className='fs-9' style={{fontSize: '9px', opacity: 0.9}}>
                    {isTableAllocated(table.id) 
                      ? `👤 ${getAllocationInfo(table.id)?.people}p`
                      : getStatusText(table.currentStatus)
                    }
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

            <div className='alert alert-info mt-3 d-flex align-items-center'>
              <i className='bi bi-hand-index-thumb fs-3 me-3'></i>
              <div>
                <strong>Modo Manual Ativado:</strong> <strong>Arraste e solte</strong> as mesas livremente para posicioná-las onde desejar (salva automaticamente). <strong>Clique</strong> na mesa para alocar/desalocar reservas rapidamente. Use as <strong>bordas coloridas e badges com iniciais</strong> para identificar mesas unidas do mesmo grupo.
              </div>
            </div>

      {/* Accordion Sistema de Cores */}
      <div className='accordion mt-3' id='accordionColorSystem'>
        <div className='accordion-item border'>
          <h2 className='accordion-header' id='headingColors'>
            <button
              className='accordion-button collapsed bg-light'
              type='button'
              data-bs-toggle='collapse'
              data-bs-target='#collapseColors'
              aria-expanded='false'
              aria-controls='collapseColors'
            >
              <i className='bi bi-lightbulb fs-5 me-3 text-warning'></i>
              <strong>Entenda o sistema de cores</strong>
            </button>
          </h2>
          <div
            id='collapseColors'
            className='accordion-collapse collapse'
            aria-labelledby='headingColors'
            data-bs-parent='#accordionColorSystem'
          >
            <div className='accordion-body'>
              <ul className='mb-0 small' style={{lineHeight: '1.8'}}>
                <li>
                  <strong className='text-success'>Verde com brilho:</strong> Mesa com reserva <strong>CONFIRMADA</strong> para esta data/hora (resultado do filtro aplicado). Esta é a prioridade máxima.
                </li>
                <li>
                  <strong style={{color: '#ff9f43'}}>Laranja:</strong> Mesa <strong>ALOCADA</strong> com reserva (promessa de ocupação - pode gerar cobrança se cliente faltar).
                </li>
                <li>
                  <strong style={{color: '#7239ea'}}>Roxo:</strong> Mesa <strong>DISPONÍVEL</strong> para novas alocações.
                </li>
                <li>
                  <strong style={{color: '#fd397a'}}>Rosa:</strong> Mesa <strong>OCUPADA</strong> neste momento (cliente presente no restaurante).
                </li>
                <li>
                  <strong style={{color: '#3699ff'}}>Azul:</strong> Mesa em <strong>LIMPEZA</strong> (aguardando liberação).
                </li>
                <li>
                  <strong style={{color: '#8b9199'}}>Cinza:</strong> Mesa <strong>INATIVA</strong> (desativada no sistema).
                </li>
              </ul>
              <p className='mb-0 mt-2 small fst-italic text-muted'>
                <i className='bi bi-info-circle me-1'></i>
                O sistema sempre prioriza mostrar as reservas confirmadas (verde) sobre os demais status da mesa.
              </p>
              
              <div className='alert alert-warning mt-3 mb-0' style={{fontSize: '13px'}}>
                <div className='d-flex align-items-start'>
                  <i className='bi bi-link-45deg fs-3 me-2'></i>
                  <div>
                    <strong>🔗 Identificação de Mesas Unidas:</strong>
                    <ul className='mt-2 mb-0' style={{lineHeight: '1.6'}}>
                      <li>
                        <strong>Borda colorida única:</strong> Mesas do mesmo grupo (mesma reserva) possuem a <strong>mesma cor de borda</strong>.
                      </li>
                      <li>
                        <strong>Badge com iniciais:</strong> No canto inferior esquerdo aparece um <strong>círculo colorido</strong> com as <strong>iniciais do cliente</strong> (ex: "MP" para Marcos Palmeira).
                      </li>
                      <li>
                        <strong>Cores únicas:</strong> Cada grupo de mesas unidas recebe uma <strong>cor diferente</strong>, nunca repetindo com outros grupos.
                      </li>
                    </ul>
                    <div className='mt-2 fst-italic'>
                      <i className='bi bi-info-circle me-1'></i>
                      Passe o mouse sobre o badge para ver o nome completo e quantidade de mesas do grupo. <strong>Arraste e solte</strong> para posicionar as mesas lado a lado manualmente.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accordion Como Alterar Status */}
      <div className='accordion mt-2' id='accordionStatusChange'>
        <div className='accordion-item border border-warning'>
          <h2 className='accordion-header' id='headingStatus'>
            <button
              className='accordion-button collapsed bg-warning bg-opacity-10'
              type='button'
              data-bs-toggle='collapse'
              data-bs-target='#collapseStatus'
              aria-expanded='false'
              aria-controls='collapseStatus'
            >
              <i className='bi bi-gear fs-5 me-3 text-warning'></i>
              <strong>Como alterar o status das mesas</strong>
            </button>
          </h2>
          <div
            id='collapseStatus'
            className='accordion-collapse collapse'
            aria-labelledby='headingStatus'
            data-bs-parent='#accordionStatusChange'
          >
            <div className='accordion-body'>
              <ul className='mb-0 small' style={{lineHeight: '1.8'}}>
                <li>
                  <strong className='text-success'>Verde (Confirmada):</strong> Acontece quando o cliente <strong>confirma e paga</strong> a reserva, ou quando o funcionário marca como confirmada no sistema.
                </li>
                <li>
                  <strong style={{color: '#ff9f43'}}>Laranja (Alocada):</strong> Mesa recebe este status quando você <strong>aloca uma reserva SEM confirmação</strong> (duplo clique → escolher reserva).
                </li>
                <li>
                  <strong style={{color: '#fd397a'}}>Rosa (Ocupada):</strong> Altere manualmente na página de <strong>Gerenciar Mesas</strong> quando o cliente chegar e sentar na mesa.
                </li>
                <li>
                  <strong style={{color: '#3699ff'}}>Azul (Limpeza):</strong> Altere manualmente na página de <strong>Gerenciar Mesas</strong> quando o cliente sair e a mesa precisar de limpeza.
                </li>
                <li>
                  <strong style={{color: '#7239ea'}}>Roxo (Disponível):</strong> Volta automaticamente após limpar ou desalocar a reserva.
                </li>
              </ul>
              <p className='mb-0 mt-2 small'>
                <i className='bi bi-hand-index-thumb me-1'></i>
                <strong>Dica de uso:</strong> <strong>Clique</strong> na mesa disponível (roxa) para alocar rapidamente uma reserva, ou clique numa mesa alocada/confirmada para liberar.
              </p>
            </div>
          </div>
        </div>
      </div>

      <QuickAllocationModal
        show={showQuickModal}
        onHide={() => {
          setShowQuickModal(false)
          setSelectedTableForModal(null)
        }}
        table={selectedTableForModal}
        allocation={selectedTableForModal ? (getAllocationInfo(selectedTableForModal.id) || null) : null}
        availableBookings={availableBookings || []}
        availableTables={tables}
        allocations={allocations}
        onAllocate={async (tableId, bookingId) => {
          if (onAllocateBooking) {
            await onAllocateBooking(tableId, bookingId)
          }
        }}
        onAllocateWalkIn={onAllocateWalkIn}
        onDeallocate={async (allocationId) => {
          if (onDeallocateBooking) {
            await onDeallocateBooking(allocationId)
          }
        }}
        onChangeStatus={onChangeAllocationStatus}
      />
    </div>
  )
}
